import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { AdminAuthContext } from '../../context/AdminAuthContext';
import { 
  LogOut, PieChart, UserCircle2, HardHat, Users, 
  Menu, BriefcaseBusiness, ChevronRight, FilePlus2, FileEdit, 
  Package, FileText, Truck, ShoppingCart, Camera,
  Activity, CalendarClock, Settings, Bot, Calendar
} from 'lucide-react';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';

const TopBar = ({ 
  currentPath: propCurrentPath,
  isCollapsed,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  ...props 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentPath } = useOutletContext() || {};
  const effectivePath = propCurrentPath || currentPath;
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [animateBar, setAnimateBar] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Aggiungi un'animazione all'ingresso della TopBar
  useEffect(() => {
    setAnimateBar(true);
    const timeout = setTimeout(() => setAnimateBar(false), 500);
    return () => clearTimeout(timeout);
  }, [location.pathname]);

  const { user, logout: logoutUser } = useContext(AuthContext);
  const { admin, logout: logoutAdmin } = useContext(AdminAuthContext);

  const isAdmin = !!admin;
  const currentUser = isAdmin ? admin : user;

  const getPageInfo = (pathname) => {
    const pathParts = pathname.split('/');
    const basePath = '/' + pathParts[1];
    const subPath = pathParts[2];
    const action = pathParts[3];
    const subAction = pathParts[4];
    const actionType = pathParts[5];
    
    // Definizione delle rotte admin e utente
    const adminRoutes = {
      '/admin': { title: 'Dashboard Admin', icon: PieChart },
      '/admin/settings': { title: 'Impostazioni Admin', icon: Settings },
      '/stato-becco': { title: 'Stato Becco', icon: Activity },
      '/gestione-eventi': { title: 'Gestione Eventi', icon: CalendarClock },
      '/gestione-amici': { title: 'Gestione Amici', icon: Users },
      '/cantieri': { 
        title: 'Gestione Cantieri', 
        icon: HardHat,
        subRoutes: {
          'nuovo': { title: 'Nuovo Cantiere', icon: FilePlus2 },
        }
      },
      '/dipendenti': { 
        title: 'Gestione Dipendenti', 
        icon: Users,
        subRoutes: {
          'nuovo': { title: 'Nuovo Dipendente', icon: FilePlus2 },
        }
      },
      '/risorse': { title: 'Gestione Risorse', icon: BriefcaseBusiness },
      '/fornitori': {
        title: 'Gestione Fornitori',
        icon: ShoppingCart,
        subRoutes: {
          'nuovo': { title: 'Nuovo Fornitore', icon: FilePlus2 },
        }
      },
      '/materiali': {
        title: 'Gestione Materiali',
        icon: Package,
        subRoutes: {
          'nuovo': { title: 'Nuovo Materiale', icon: FilePlus2 },
        }
      },
      '/noleggi': {
        title: 'Gestione Noleggi',
        icon: Truck,
        subRoutes: {
          'nuovo': { title: 'Nuovo Noleggio', icon: FilePlus2 },
        }
      },
      '/documenti': {
        title: 'Gestione Documenti',
        icon: FileText,
        subRoutes: {
          'nuovo': { title: 'Nuovo Documento', icon: FilePlus2 },
        }
      }
    };

    const userRoutes = {
      '/dashboard': { title: 'Dashboard', icon: PieChart },
      '/settings': { title: 'Impostazioni', icon: Settings },
      '/ai-chat': { title: 'Chat AI', icon: Bot },
      '/eventi': { title: 'Eventi', icon: Calendar },
      '/cantieri-assegnati': {
        title: 'I Miei Cantieri',
        icon: HardHat,
        subRoutes: {
          'azioni': { title: 'Azioni Cantiere', icon: FileText }
        }
      },
      '/documenti': { title: 'I Miei Documenti', icon: FileText }
    };

    const routes = isAdmin ? adminRoutes : userRoutes;
    const baseRoute = routes[basePath] || {
      title: isAdmin ? 'Dashboard Admin' : 'Dashboard',
      icon: isAdmin ? PieChart : UserCircle2
    };

    // Funzione generica per gestire le sottorotte
    const handleSubRoutes = (baseRoute, subPath, action, itemName) => {
      if (subPath) {
        if (action === 'modifica') {
          return {
            base: { title: baseRoute.title, icon: baseRoute.icon },
            sub: { title: `Modifica ${itemName}`, icon: FileEdit }
          };
        } else if (baseRoute.subRoutes[subPath]) {
          const subRoute = baseRoute.subRoutes[subPath];
          return {
            base: { title: baseRoute.title, icon: baseRoute.icon },
            sub: { title: subRoute.title, icon: subRoute.icon }
          };
        } else {
          return {
            base: { title: baseRoute.title, icon: baseRoute.icon },
            sub: { title: `Dettaglio ${itemName}`, icon: baseRoute.icon }
          };
        }
      }
    };

    // Gestione delle rotte specifiche
    if (basePath === '/cantieri' || basePath === '/cantieri-assegnati') {
      if (action === 'azioni') {
        if (subAction === 'nuovo') {
          const actionName = actionType === 'media' ? 'Media' : 'Report Contabilità';
          return {
            base: { title: baseRoute.title, icon: baseRoute.icon },
            sub: { title: 'Dettaglio Cantiere', icon: HardHat },
            action: { title: `Nuovo ${actionName}`, icon: actionType === 'media' ? Camera : FileText }
          };
        } else if (subAction && pathParts[5] === 'modifica') {
          return {
            base: { title: baseRoute.title, icon: baseRoute.icon },
            sub: { title: 'Dettaglio Cantiere', icon: HardHat },
            action: { title: 'Modifica Azione', icon: FileEdit }
          };
        } else if (subAction && pathParts[5] === 'visualizza') {
          return {
            base: { title: baseRoute.title, icon: baseRoute.icon },
            sub: { title: 'Dettaglio Cantiere', icon: HardHat },
            action: { title: 'Visualizza Azione', icon: FileText }
          };
        }
      }
      
      return handleSubRoutes(baseRoute, subPath, action, 'Cantiere') || {
        base: { title: baseRoute.title, icon: baseRoute.icon },
        sub: null
      };
    }

    if (basePath === '/dipendenti') {
      return handleSubRoutes(baseRoute, subPath, action, 'Dipendente') || {
        base: { title: baseRoute.title, icon: baseRoute.icon },
        sub: null
      };
    }

    if (basePath === '/fornitori') {
      return handleSubRoutes(baseRoute, subPath, action, 'Fornitore') || {
        base: { title: baseRoute.title, icon: baseRoute.icon },
        sub: null
      };
    }

    if (basePath === '/materiali') {
      if (subPath === 'nuovo-documento') {
        return {
          base: { title: baseRoute.title, icon: baseRoute.icon },
          sub: { title: 'Nuovo Documento', icon: FilePlus2 }
        };
      }
      return handleSubRoutes(baseRoute, subPath, action, 'Materiale') || {
        base: { title: baseRoute.title, icon: baseRoute.icon },
        sub: null
      };
    }

    if (basePath === '/noleggi') {
      return handleSubRoutes(baseRoute, subPath, action, 'Noleggio') || {
        base: { title: baseRoute.title, icon: baseRoute.icon },
        sub: null
      };
    }

    if (basePath === '/documenti') {
      return handleSubRoutes(baseRoute, subPath, action, 'Documento') || {
        base: { title: baseRoute.title, icon: baseRoute.icon },
        sub: null
      };
    }

    // Caso default
    return {
      base: { title: baseRoute.title, icon: baseRoute.icon },
      sub: null
    };
  };

  const logout = () => {
    if (admin) {
      logoutAdmin();
      navigate('/admin/login');
    } else {
      logoutUser();
      navigate('/login');
    }
  };

  const contextConfigs = {
    admin: {
      variant: 'primary',
      hoverEffect: 'hover:bg-light-primary-container dark:hover:bg-dark-primary-container'
    },
    user: {
      variant: 'secondary',
      hoverEffect: 'hover:bg-light-secondary-container dark:hover:bg-dark-secondary-container'
    }
  };

  const themeClasses = {
    primary: {
      nav: 'bg-light-primary dark:bg-dark-primary shadow-surface-container',
      text: 'text-light-on-primary dark:text-dark-on-primary',
      hover: 'hover:bg-light-primary-container dark:hover:bg-dark-primary-container',
      icon: 'text-light-on-primary dark:text-dark-on-primary',
      breadcrumb: 'after:bg-light-on-primary dark:after:bg-dark-on-primary'
    },
    secondary: {
      nav: 'bg-light-secondary dark:bg-dark-secondary shadow-surface-container',
      text: 'text-light-on-secondary dark:text-dark-on-secondary',
      hover: 'hover:bg-light-secondary-container dark:hover:bg-dark-secondary-container',
      icon: 'text-light-on-secondary dark:text-dark-on-secondary',
      breadcrumb: 'after:bg-light-on-secondary dark:after:bg-dark-on-secondary'
    }
  };

  const config = isAdmin ? contextConfigs.admin : contextConfigs.user;
  const classes = themeClasses[config.variant];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const renderBreadcrumb = () => {
    const pageInfo = getPageInfo(location.pathname);
    
    return (
      <div className="flex items-center gap-2">
        <div 
          onClick={() => navigate(`/${location.pathname.split('/')[1]}`)}
          className={`
            flex items-center gap-2 cursor-pointer
            group transition-all duration-300 px-2 py-1 rounded-md
            ${classes.hover}
          `}
        >
          <pageInfo.base.icon className={`h-8 w-8 ${classes.icon}`} />
          <span className={`
            relative ml-2 text-xl font-bold ${classes.text}
            after:content-[''] after:absolute after:w-full after:scale-x-0 
            after:h-[2px] after:bottom-[-2px] after:left-0
            ${classes.breadcrumb}
            after:transition-transform after:duration-300 after:ease-out
            group-hover:after:scale-x-100
          `}>
            {pageInfo.base.title}
          </span>
        </div>
        
        {pageInfo.sub && (
          <>
            <ChevronRight className={`h-5 w-5 ${classes.text}`} />
            <div 
              onClick={() => {
                const basePath = isAdmin ? '/cantieri' : '/cantieri-assegnati';
                navigate(`${basePath}/${location.pathname.split('/')[2]}`);
              }}
              className={`
                flex items-center gap-2 cursor-pointer
                px-2 py-1 rounded-md
                group transition-all duration-300
                ${classes.text}
                ${classes.hover}
              `}
            >
              <pageInfo.sub.icon className={`h-6 w-6 ${classes.icon}`} />
              <span className={`ml-2 text-lg font-bold relative
                after:content-[''] after:absolute after:w-full after:scale-x-0 
                after:h-[2px] after:bottom-[-2px] after:left-0
                ${classes.breadcrumb}
                after:transition-transform after:duration-300 after:ease-out
                group-hover:after:scale-x-100
              `}>
                {pageInfo.sub.title}
              </span>
            </div>
          </>
        )}

        {pageInfo.action && (
          <>
            <ChevronRight className={`h-5 w-5 ${classes.text}`} />
            <div className={`
              flex items-center gap-2
              px-2 py-1 rounded-md
              ${classes.text}
            `}>
              <pageInfo.action.icon className={`h-6 w-6 ${classes.icon}`} />
              <span className="ml-2 text-lg font-bold">
                {pageInfo.action.title}
              </span>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <nav 
      className={`
        fixed top-0 right-0
        h-16 z-40
        flex items-center
        transition-all duration-300
        ${classes.nav}
        ${animateBar ? 'animate-slide-down' : ''}
        pr-3 sm:pr-4 md:pr-6
      `}
      style={{
        left: isDesktop ? (isCollapsed ? '5rem' : '18rem') : '0',
        width: isDesktop ? `calc(100% - ${isCollapsed ? '5rem' : '18rem'})` : '100%'
      }}
    >
      <div className="w-full px-4 flex justify-between items-center">
        <div className="flex items-center">
          <button 
            className={`lg:hidden p-2 mr-2 rounded-full ${classes.hover}`}
            onClick={toggleMobileMenu}
          >
            <Menu className={classes.text} />
          </button>
          {renderBreadcrumb()}
        </div>
        <div className="flex items-center space-x-4">
          <span className={`font-medium hidden sm:inline ${classes.text}`}>
            {isAdmin 
              ? (currentUser?.nome && currentUser?.cognome 
                  ? `${currentUser.nome} ${currentUser.cognome}`
                  : currentUser?.username)
              : `${currentUser?.firstName} ${currentUser?.lastName}`
            }
          </span>
          {(user || admin) && (
            <button
              onClick={logout}
              className={`
                inline-flex items-center px-4 py-2
                rounded-md
                hover:bg-light-error dark:hover:bg-dark-error
                hover:text-light-on-error dark:hover:text-dark-on-error
                transition-colors duration-200
                ${classes.text}
              `}
            >
              <LogOut className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default TopBar;